import React, { useEffect, useMemo, useState } from "react";
import "../styles/CarpetPickup.css";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import {
  createBooking,
  resetState,
} from "../features/booking/bookingSlice";
import { toast } from "react-toastify";
import { useRef } from "react";

const rate = 30;
const FT_TO_M = 0.3048;

const carpets = [
  { label: "5 x 8 ft", width: 5, height: 8 },
  { label: "6 x 10 ft", width: 6, height: 10 },
  { label: "7 x 10 ft", width: 7, height: 10 },
  { label: "8 x 11 ft", width: 8, height: 11 },
  { label: "9 x 12 ft", width: 9, height: 12 },
  { label: "Extra Large", width: 9, height: 12, fixedPrice: 3240 },
];

const estates = [
  "Runda Park",
  "Runda Mumwe",
  "Runda Evergreen",
  "Ridgeways",
  "Fourways Junction",
  "Garden Estate",
  "Thindigua",
  "Kiambu Road",
  "Muthaiga North",
  "Rosslyn",
  "Rosslyn Riviera",
  "Gigiri",
  "Two Rivers",
  "Other",
];

const formatCarpetOption = (item) => {
  if (item.label === "Extra Large") {
    return "Extra Large • Above 9 x 12 ft • Above 2.7 x 3.6 m - KSh 3,240";
  }

  const widthM = (item.width * FT_TO_M).toFixed(1);
  const heightM = (item.height * FT_TO_M).toFixed(1);
  const price = item.width * item.height * rate;

  return `${item.width} x ${item.height} ft • ${widthM} x ${heightM} m - KSh ${price.toLocaleString()}`;
};

const schema = yup.object().shape({
  name: yup.string().required("Full name is required"),
  phone: yup.string().required("Phone number is required"),
  carpetSize: yup.string().required("Carpet size is required"),
  numberOfCarpets: yup
    .number()
    .typeError("Number of carpets is required")
    .min(1, "At least 1 carpet is required")
    .required("Number of carpets is required"),
  apartment: yup.string().required("Apartment / Estate is required"),
  houseNumber: yup.string().required("House / Door number is required"),
  locationNotes: yup.string().required("Location notes are required"),
  preferredDate: yup.string().required("Pickup date is required"),
  preferredTime: yup.string().required("Pickup time is required"),
});

const CarpetPickup = () => {
  const dispatch = useDispatch();

  const { isLoading, isSuccess, isError, message } = useSelector(
    (state) => state.booking
  );

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    carpetSize: "5 x 8 ft",
    numberOfCarpets: 1,
    apartment: "",
    houseNumber: "",
    locationNotes: "",
    preferredDate: "",
    preferredTime: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});

  const selectedPrice = useMemo(() => {
    const selected = carpets.find((item) => item.label === formData.carpetSize);

    if (!selected) return 0;

    const basePrice = selected.fixedPrice
      ? selected.fixedPrice
      : selected.width * selected.height * rate;

    return basePrice * Number(formData.numberOfCarpets || 1);
  }, [formData.carpetSize, formData.numberOfCarpets]);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Carpet pickup booked successfully!");

      setFormData({
        name: "",
        phone: "",
        email: "",
        carpetSize: "5 x 8 ft",
        numberOfCarpets: 1,
        apartment: "",
        houseNumber: "",
        locationNotes: "",
        preferredDate: "",
        preferredTime: "",
        notes: "",
      });

      setErrors({});
      dispatch(resetState());
    }

    if (isError) {
      toast.error(message || "Something went wrong");
      dispatch(resetState());
    }
  }, [isSuccess, isError, message, dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await schema.validate(formData, { abortEarly: false });

      const bookingData = {
        serviceType: "carpet",
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        carpetSize: formData.carpetSize,
        numberOfCarpets: Number(formData.numberOfCarpets),
        apartment: formData.apartment,
        houseNumber: formData.houseNumber,
        locationNotes: formData.locationNotes,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime,
        notes: formData.notes,
        price: selectedPrice,
      };

      await dispatch(createBooking(bookingData)).unwrap();
    } catch (error) {
      if (error.inner) {
        const validationErrors = {};

        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });

        setErrors(validationErrors);
        toast.error("Please fill in all required fields.");
      } else {
        toast.error(error || "Something went wrong");
      }
    }
  };

  const servicesRef = useRef(null);
  
  useEffect(() => {
    const revealElements =
      servicesRef.current?.querySelectorAll("[data-reveal]") || [];
  
    const scrollReveal = () => {
      revealElements.forEach((el) => {
        const elementIsInScreen =
          el.getBoundingClientRect().top < window.innerHeight / 1.15;
  
        if (elementIsInScreen) {
          el.classList.add("revealed");
        } else {
          el.classList.remove("revealed");
        }
      });
    };
  
    window.addEventListener("scroll", scrollReveal);
    scrollReveal(); // run on load
  
    return () => {
      window.removeEventListener("scroll", scrollReveal);
    };
  }, []);

  return (
    <main className="carpet-pickup-page" ref={servicesRef}>
      <section className="carpet-pickup-hero">
        <span data-reveal>Schedule a pickup</span>
        <h1 data-reveal>Carpet Cleaning Pickup</h1>
        <p data-reveal>
          Book your carpet pickup and delivery service in minutes. We clean,
          remove stains, and leave your carpet fresh.
        </p>
      </section>

      <section className="carpet-pickup-pricing">
        <h2 data-reveal>Carpet Price List</h2>

        <div className="carpet-pickup-table-wrapper" data-reveal>
          <table className="carpet-pickup-table">
            <thead>
              <tr>
                <th>Size (ft)</th>
                <th>Size (m)</th>
                <th>Price (KSh)</th>
              </tr>
            </thead>

            <tbody>
              {carpets.map((item, index) => {
                const widthM = (item.width * FT_TO_M).toFixed(1);
                const heightM = (item.height * FT_TO_M).toFixed(1);

                if (item.label === "Extra Large") {
                  return (
                    <tr key={index}>
                      <td>Above 9 x 12</td>
                      <td>Above 2.7 x 3.6</td>
                      <td>KSh 3,240</td>
                    </tr>
                  );
                }

                const price = item.width * item.height * rate;

                return (
                  <tr key={index}>
                    <td>
                      {item.width} x {item.height}
                    </td>
                    <td>
                      {widthM} x {heightM}
                    </td>
                    <td>KSh {price.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="carpet-pickup-form-section">
        <div className="form-info">
          <span data-reveal>Pickup form</span>
          <h2 data-reveal>Book Carpet Pickup</h2>
          <p data-reveal>Fill in your details and we’ll confirm your pickup schedule.</p>
        </div>

        <form className="carpet-pickup-form" onSubmit={handleSubmit} data-reveal>
          <div className="form-row">
            <div className="form-group">
              <label>Full Name</label>
              <input
                type="text"
                name="name"
                placeholder="Your name"
                value={formData.name}
                onChange={handleChange}
              />
              {errors.name && <small className="error-text">{errors.name}</small>}
            </div>

            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                name="phone"
                placeholder="+254..."
                value={formData.phone}
                onChange={handleChange}
              />
              {errors.phone && <small className="error-text">{errors.phone}</small>}
            </div>
          </div>

          <div className="form-group">
            <label>Email Address Optional</label>
            <input
              type="email"
              name="email"
              placeholder="you@example.com"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Carpet Size</label>
              <select
                name="carpetSize"
                value={formData.carpetSize}
                onChange={handleChange}
              >
                {carpets.map((item) => (
                  <option key={item.label} value={item.label}>
                    {formatCarpetOption(item)}
                  </option>
                ))}
              </select>
              {errors.carpetSize && (
                <small className="error-text">{errors.carpetSize}</small>
              )}
            </div>

            <div className="form-group">
              <label>Number of Carpets</label>
              <input
                type="number"
                min="1"
                name="numberOfCarpets"
                placeholder="e.g. 2"
                value={formData.numberOfCarpets}
                onChange={handleChange}
              />
              {errors.numberOfCarpets && (
                <small className="error-text">{errors.numberOfCarpets}</small>
              )}
            </div>
          </div>

          <div className="form-total">
            Estimated Total: <strong>KSh {selectedPrice.toLocaleString()}</strong>
          </div>

          <div className="form-group">
        <label>Apartment / Estate</label>
        <input
          type="text"
          name="apartment"
          placeholder="E.g., Runda Evergreen"
          value={formData.apartment}
          onChange={handleChange}
        />
        {errors.apartment && (
          <small className="error-text">{errors.apartment}</small>
        )}
      </div>

          <div className="form-group">
            <label>House / Door Number</label>
            <input
              type="text"
              name="houseNumber"
              placeholder="E.g., House 7"
              value={formData.houseNumber}
              onChange={handleChange}
            />
            {errors.houseNumber && (
              <small className="error-text">{errors.houseNumber}</small>
            )}
          </div>

          <div className="form-group">
        <label>Location Notes</label>
        <input
          type="text"
          name="locationNotes"
          placeholder="E.g., opposite Naivas"
          value={formData.locationNotes}
          onChange={handleChange}
        />
        {errors.locationNotes && (
          <small className="error-text">{errors.locationNotes}</small>
        )}
      </div>

          <div className="form-row">
            <div className="form-group">
              <label>Preferred Pickup Date</label>
              <input
                type="date"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
              />
              {errors.preferredDate && (
                <small className="error-text">{errors.preferredDate}</small>
              )}
            </div>

            <div className="form-group">
              <label>Preferred Pickup Time</label>
              <input
                type="time"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
              />
              {errors.preferredTime && (
                <small className="error-text">{errors.preferredTime}</small>
              )}
            </div>
          </div>

          <div className="form-group">
            <label>Additional Notes</label>
            <textarea
              name="notes"
              rows="5"
              placeholder="Any stains, special instructions, or delivery notes?"
              value={formData.notes}
              onChange={handleChange}
            ></textarea>
          </div>

          <button type="submit" className="submit-btn" disabled={isLoading}>
            {isLoading ? "Submitting..." : "Book Pickup"}
          </button>
        </form>
      </section>
    </main>
  );
};

export default CarpetPickup;